/**
 * Per-pile total readouts.
 *
 * Without these the player is doing blackjack arithmetic under a 180-second
 * clock, which is the least interesting part of the game. A badge floats above
 * each pile — clear of the downward fan, so it never gets covered as the stack
 * grows — carrying three pieces of information, in descending order of how
 * quickly they read:
 *
 *   1. A gauge filling toward 21. Shape, not digits: four of them side by side
 *      answer "where is there room?" in a single glance.
 *   2. Colour by headroom, on thresholds blackjack players already own —
 *      calm under 12, gold 12–16, rose 17+, mint on exactly 21.
 *   3. The number itself, struck into the plaque.
 *
 * Soft piles show the promoted total large with the hard total small beneath,
 * because a player reading only "17" on A♠6♥ will not realise a four still
 * lands safely — the ace quietly drops back to 1. Hard piles show one number,
 * so the second figure only ever appears when it is saying something.
 *
 * ## Lighting
 *
 * Every badge is a physical plaque lying above the felt, and it is lit by the
 * same virtual light as the cards. `LIGHT` gives each badge its own direction —
 * pile 0 sits left of the key and throws its shadow down-left, pile 3 sits well
 * to the right and throws down-right — and that one direction drives *all* of
 * it: the drop shadow's offset, the plaque's surface gradient, which edge of
 * the hairline catches the highlight, the deboss under the number and the lit
 * rim above it. When the readout pops, the plaque lifts; `SHADOW` then makes
 * the shadow longer, softer and weaker exactly as it does for a card in the
 * air. Nothing here is allowed to disagree with anything else.
 */

import {
  BlurFilter,
  Container,
  FillGradient,
  Graphics,
  Text,
  type DestroyOptions,
  type TextStyleOptions,
} from 'pixi.js';
import { gsap } from 'gsap';

import { bus as sharedBus, type EventBus } from '@/core/bus';
import * as tokens from '@/core/tokens';
import { LIGHT, SHADOW, TOTAL_BADGE, pilePosition } from '@/pixi/layout';
import { PILE_COUNT, type GameSnapshot, type PileTotals as PileTotalValues } from '@/game/types';
import { TARGET, bestTotal } from '@/game/rules';

const { palette, motion } = tokens;

/** Display face, as a CSS stack — Pixi splits it on commas and honours it. */
const DISPLAY_FONT = tokens.type.display;

/** `0.06em` from the type tokens, as a multiplier of the font size. */
const TRACKING = Number.parseFloat(tokens.type.displayTracking);

/**
 * Blackjack's own thresholds. 12 is where a ten starts to hurt, 17 is where
 * most tables stand. Players already read the game through these numbers, so
 * the gauge is notched at them and the colour steps on them.
 */
const CAUTION_AT = 12;
const DANGER_AT = 17;

/** Virtual height of a resting plaque above the felt, in design units. */
const LIFT_REST = 26;
/** Height at the top of a pop. Longer, softer, weaker shadow — see SHADOW. */
const LIFT_POP = 62;

/** Pop overshoot for an ordinary card landing, and for a pile clearing. */
const POP_PLAY = 0.13;
const POP_CLEAR = 0.28;

/**
 * Pixi centres text on the full line box, which for a digits-only string sits
 * high because the descender space is empty. Nudge down by this fraction of the
 * font size to put the glyphs on the optical centre instead.
 */
const OPTICAL_NUDGE = 0.055;

/** Low enough to be invisible, high enough not to be culled. */
const WARM_ALPHA = 0.002;

/** Badge geometry, all of it proportional to TOTAL_BADGE so it scales as one. */
const BADGE = {
  width: TOTAL_BADGE.width,
  height: TOTAL_BADGE.height,
  halfWidth: TOTAL_BADGE.width / 2,
  halfHeight: TOTAL_BADGE.height / 2,
  radius: TOTAL_BADGE.height * 0.26,
  /** Number size when the pile is hard: one figure, so it can be big. */
  hardSize: TOTAL_BADGE.height * 0.66,
  /** Number size when a second figure has to fit underneath. */
  softSize: TOTAL_BADGE.height * 0.56,
  subSize: TOTAL_BADGE.height * 0.245,
} as const;

/** The headroom gauge, along the bottom lip of the plaque. */
const GAUGE = {
  height: TOTAL_BADGE.height * 0.085,
  x: -BADGE.halfWidth + TOTAL_BADGE.width * 0.115,
  width: TOTAL_BADGE.width * 0.77,
  y: BADGE.halfHeight - TOTAL_BADGE.height * 0.135 - TOTAL_BADGE.height * 0.085,
} as const;

/** Vertical centre of the space the numbers get, above the gauge. */
const READOUT_MID = (-BADGE.halfHeight + TOTAL_BADGE.height * 0.06 + (GAUGE.y - TOTAL_BADGE.height * 0.07)) / 2;

type RiskLevel = 'calm' | 'warm' | 'hot' | 'perfect';

/**
 * "Calm" is deliberately not a signal colour — a cool pewter, so a low pile
 * reads as "nothing to see here" and never competes with the gold, rose and
 * mint that mean something. Kept off the saturated violet, which belongs to
 * the wild alone.
 */
const CALM = mix(mix(palette.white, palette.violetLight, 0.34), palette.plum, 0.18);

const RISK_COLOUR: Readonly<Record<RiskLevel, number>> = {
  calm: CALM,
  warm: palette.gold,
  hot: palette.rose,
  perfect: palette.mint,
};

function riskOf(total: number): RiskLevel {
  if (total === TARGET) return 'perfect';
  if (total >= DANGER_AT) return 'hot';
  if (total >= CAUTION_AT) return 'warm';
  return 'calm';
}

/** Linear blend of two packed 0xRRGGBB colours. */
function mix(a: number, b: number, t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const r = Math.round(ar + (((b >> 16) & 0xff) - ar) * k);
  const g = Math.round(ag + (((b >> 8) & 0xff) - ag) * k);
  const bl = Math.round(ab + ((b & 0xff) - ab) * k);
  return (r << 16) | (g << 8) | bl;
}

/** Gradient stops need alpha, and only strings carry it through Pixi's parser. */
function rgba(colour: number, alpha: number): string {
  const a = Math.round((alpha < 0 ? 0 : alpha > 1 ? 1 : alpha) * 255);
  return `#${colour.toString(16).padStart(6, '0')}${a.toString(16).padStart(2, '0')}`;
}

interface Vec {
  x: number;
  y: number;
}

interface ShadowCast {
  /** Offset from the caster, in design units. */
  dx: number;
  dy: number;
  /** Direction of that offset, for text drop shadows. */
  angle: number;
  blur: number;
  alpha: number;
}

/**
 * Where an object at (x, y) sitting `lift` units above the felt throws its
 * shadow. Direction is the ray from LIGHT through the object, flattened onto
 * the table; length, blur and alpha all come from SHADOW. Because the same
 * light drives the plaque's shading, the two cannot drift apart.
 */
function castShadow(x: number, y: number, lift: number): ShadowCast {
  const vx = x - LIGHT.x;
  const vy = y - LIGHT.y;
  const len = Math.hypot(vx, vy) || 1;
  // Ground distance from the light stretches the shadow; LIGHT.z keeps a tall
  // light nearly parallel, so the stretch stays gentle across the table.
  const reach = lift * SHADOW.offsetScale * (1 + len / LIGHT.z);
  return {
    dx: (vx / len) * reach,
    dy: (vy / len) * reach,
    angle: Math.atan2(vy, vx),
    blur: SHADOW.blurBase + lift * SHADOW.blurPerLift,
    // Higher up means the light spreads over more table: softer *and* weaker.
    alpha: SHADOW.alphaBase / (1 + (lift / LIGHT.z) * 6),
  };
}

/** Unit vector pointing away from the light, for a badge at (x, y). */
function awayFromLight(x: number, y: number): Vec {
  const vx = x - LIGHT.x;
  const vy = y - LIGHT.y;
  const len = Math.hypot(vx, vy) || 1;
  return { x: vx / len, y: vy / len };
}

/**
 * A linear gradient running across the plaque along the light axis: offset 0
 * sits on the lit edge, offset 1 on the shadowed one.
 *
 * The axis is given in the plaque's own geometry units and the gradient is
 * declared `global`, not `local`. Pixi 8.19 scales a local-space gradient's
 * origin by the gradient texture size, which pushes the whole ramp off the
 * shape and leaves it flat on its first colour stop — a dead giveaway, since
 * the plaque then renders at its *lightest* value. Global space maps the axis
 * straight onto the geometry, which is what is wanted here anyway: the plaque
 * is drawn centred on its own origin, so these coordinates are exact.
 */
function alongLight(dir: Vec, stops: { offset: number; color: string }[]): FillGradient {
  // Half-extent of the plaque along `dir` — the ramp spans corner to corner.
  const reach = (Math.abs(dir.x) * BADGE.width + Math.abs(dir.y) * BADGE.height) / 2;
  return new FillGradient({
    type: 'linear',
    textureSpace: 'global',
    start: { x: -dir.x * reach, y: -dir.y * reach },
    end: { x: dir.x * reach, y: dir.y * reach },
    colorStops: stops,
  });
}

/** The plaque body: dark enough to carry a white number over a pale card. */
function plaqueGradient(dir: Vec): FillGradient {
  return alongLight(dir, [
    { offset: 0, color: rgba(mix(palette.plum, palette.white, 0.13), 1) },
    { offset: 0.44, color: rgba(mix(palette.feltDeep, palette.plum, 0.5), 1) },
    { offset: 1, color: rgba(mix(palette.ink, palette.plum, 0.16), 1) },
  ]);
}

/** Sheen on the lit face, and LIGHT's cool bounce on the far one. */
function glossGradient(dir: Vec): FillGradient {
  return alongLight(dir, [
    { offset: 0, color: rgba(mix(LIGHT.colour, palette.white, 0.45), 0.17) },
    { offset: 0.34, color: rgba(palette.white, 0.03) },
    { offset: 0.62, color: rgba(LIGHT.fill.colour, 0) },
    { offset: 1, color: rgba(LIGHT.fill.colour, LIGHT.fill.intensity * 0.24) },
  ]);
}

/**
 * The edge. White here, tinted by risk at runtime — bright where the light
 * strikes it, all but gone on the shadow side. A hairline, never a border.
 */
function hairlineGradient(dir: Vec): FillGradient {
  return alongLight(dir, [
    { offset: 0, color: rgba(palette.white, 0.88) },
    { offset: 0.45, color: rgba(palette.white, 0.28) },
    { offset: 1, color: rgba(palette.white, 0.09) },
  ]);
}

/**
 * Struck-metal ramp for the number: lit crown, a hard strike line across the
 * middle, a glint just under it, then a dark foot. One per risk level, cached
 * for the life of the page — colour steps rather than sliding, which reads as
 * the plate being re-struck.
 */
const METAL_CACHE = new Map<RiskLevel, FillGradient>();

function metalFor(risk: RiskLevel): FillGradient {
  const cached = METAL_CACHE.get(risk);
  if (cached) return cached;
  const tint = RISK_COLOUR[risk];
  const gradient = new FillGradient({
    type: 'linear',
    textureSpace: 'local',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    colorStops: [
      { offset: 0, color: rgba(mix(tint, palette.white, 0.88), 1) },
      { offset: 0.33, color: rgba(mix(tint, palette.white, 0.5), 1) },
      { offset: 0.5, color: rgba(mix(tint, palette.ink, 0.34), 1) },
      { offset: 0.57, color: rgba(mix(tint, palette.white, 0.96), 1) },
      { offset: 0.82, color: rgba(mix(tint, palette.white, 0.3), 1) },
      { offset: 1, color: rgba(mix(tint, palette.ink, 0.5), 1) },
    ],
  });
  METAL_CACHE.set(risk, gradient);
  return gradient;
}

function numberStyle(size: number, risk: RiskLevel, shadowAngle: number): TextStyleOptions {
  return {
    fontFamily: DISPLAY_FONT,
    fontSize: size,
    letterSpacing: size * TRACKING,
    align: 'center',
    fill: metalFor(risk),
    // The debossed edge, thrown by the same light as everything else.
    dropShadow: {
      color: palette.ink,
      alpha: 0.72,
      angle: shadowAngle,
      distance: size * 0.05,
      blur: size * 0.055,
    },
  };
}

/** The lit rim, sat behind the number and offset into the light. */
function litStyle(size: number): TextStyleOptions {
  return {
    fontFamily: DISPLAY_FONT,
    fontSize: size,
    letterSpacing: size * TRACKING,
    align: 'center',
    fill: mix(palette.white, LIGHT.colour, 0.5),
  };
}

function subStyle(shadowAngle: number): TextStyleOptions {
  return {
    fontFamily: DISPLAY_FONT,
    fontSize: BADGE.subSize,
    letterSpacing: BADGE.subSize * TRACKING,
    align: 'center',
    fill: palette.white,
    dropShadow: {
      color: palette.ink,
      alpha: 0.6,
      angle: shadowAngle,
      distance: BADGE.subSize * 0.07,
      blur: BADGE.subSize * 0.08,
    },
  };
}

/** How a pile's badge should be dismissed once the pile clears. */
type Flourish = 'clear' | 'bust';

/** One plaque. Owns its own light direction, gradients and animation state. */
class TotalBadge extends Container {
  #dir: Vec;
  #shadowAngle: number;

  readonly #shadow = new Graphics();
  readonly #shadowBlur: BlurFilter;
  /** Everything that pops. The shadow stays outside so it can lengthen. */
  readonly #body = new Container();
  readonly #plaque = new Graphics();
  readonly #gloss = new Graphics();
  readonly #hairline = new Graphics();
  readonly #track = new Graphics();
  readonly #glow = new Graphics();
  readonly #gauge = new Graphics();
  readonly #caret = new Graphics();
  readonly #primaryLit = new Text({ text: '', anchor: 0.5 });
  readonly #primary = new Text({ text: '', anchor: 0.5 });
  readonly #sub = new Text({ text: '', anchor: 0.5 });

  readonly #gradients: FillGradient[] = [];
  #lit: Vec;

  /** Animated state. Tweened through proxies so gsap drives one redraw. */
  readonly #pop = { scale: 1, lift: LIFT_REST };
  readonly #paint = { fill: 0, colour: CALM };

  #primaryValue: number | null = null;
  #hardUnder: number | null = null;
  #risk: RiskLevel = 'calm';
  #revealed = false;
  #styledRisk: RiskLevel | null = null;
  #styledSoft: boolean | null = null;
  #styleDirty = true;
  #hold: gsap.core.Tween | null = null;

  readonly #index: number;

  constructor(index: number) {
    super({ label: `pile-total-${index}` });

    this.#index = index;
    const home = pilePosition(index);
    this.position.set(home.x, home.y + TOTAL_BADGE.offsetY);
    this.visible = false;
    this.alpha = 0;

    this.#dir = awayFromLight(this.x, this.y);
    this.#lit = { x: -this.#dir.x * 1.6, y: -this.#dir.y * 1.6 };
    this.#shadowAngle = castShadow(this.x, this.y, LIFT_REST).angle;

    this.#shadowBlur = new BlurFilter({ strength: SHADOW.blurBase, quality: 3 });
    this.#shadow.filters = [this.#shadowBlur];
    this.#shadow
      .roundRect(-BADGE.halfWidth, -BADGE.halfHeight, BADGE.width, BADGE.height, BADGE.radius)
      .fill(palette.ink);

    this.#glow.filters = [new BlurFilter({ strength: GAUGE.height * 1.1, quality: 3 })];
    this.#glow.blendMode = 'add';
    this.#glow.alpha = 0.5;

    this.#drawPlaque();
    this.#drawTrack();
    this.#drawCaret();

    this.#body.addChild(
      this.#plaque,
      this.#gloss,
      this.#track,
      this.#glow,
      this.#gauge,
      this.#hairline,
      this.#primaryLit,
      this.#primary,
      this.#caret,
      this.#sub,
    );
    this.addChild(this.#shadow, this.#body);

    this.#primaryLit.alpha = 0.32;
    this.#sub.visible = false;
    this.#caret.visible = false;

    this.#applyPop();
    this.#applyPaint();
  }

  /**
   * Show `totals`, or hide the badge when `totals` is null (empty pile).
   * Idempotent: re-applying the same numbers does not re-pop.
   */
  /**
   * Move to the pile's new home after an orientation change.
   *
   * The lit direction and the shadow angle are re-derived rather than kept,
   * because both are functions of where this badge sits relative to the one
   * virtual light — a badge that moves from the left of the table to the right
   * has to be lit from the other side or the depth stops being consistent.
   */
  relayout(): void {
    const home = pilePosition(this.#index);
    this.position.set(home.x, home.y + TOTAL_BADGE.offsetY);
    this.#dir = awayFromLight(this.x, this.y);
    this.#lit = { x: -this.#dir.x * 1.6, y: -this.#dir.y * 1.6 };
    this.#shadowAngle = castShadow(this.x, this.y, LIFT_REST).angle;
  }

  apply(totals: PileTotalValues | null, animate: boolean): void {
    this.#cancelHold();

    if (!totals) {
      this.#hide(animate);
      return;
    }

    const primary = bestTotal(totals);
    // Only a genuinely soft pile earns the second figure: the ace is currently
    // worth 11, and dropping it back to 1 would leave a different number.
    const hardUnder = totals.soft <= TARGET && totals.soft !== totals.hard ? totals.hard : null;
    const changed = primary !== this.#primaryValue || hardUnder !== this.#hardUnder;

    const wasHidden = !this.#revealed;
    if (!changed && !wasHidden) {
      // Same numbers, already on screen — every other event in the round would
      // otherwise restart four colour tweens for nothing.
      return;
    }

    this.#primaryValue = primary;
    this.#hardUnder = hardUnder;
    this.#risk = riskOf(primary);
    this.#refreshText();
    this.#show(animate);

    const colour = RISK_COLOUR[this.#risk];
    const fill = Math.min(primary / TARGET, 1);

    gsap.killTweensOf(this.#paint);
    if (!animate || wasHidden) {
      this.#paint.fill = fill;
      this.#paint.colour = colour;
      this.#applyPaint();
    } else {
      const from = this.#paint.colour;
      const blend = { t: 0 };
      gsap.to(this.#paint, {
        fill,
        duration: motion.deal * 0.86,
        ease: motion.travel,
        onUpdate: () => this.#applyPaint(),
      });
      gsap.to(blend, {
        t: 1,
        duration: motion.ui,
        ease: 'power2.out',
        onUpdate: () => {
          this.#paint.colour = mix(from, colour, blend.t);
          this.#applyPaint();
        },
      });
    }

    if (animate && !wasHidden) this.#popTo(POP_PLAY);
  }

  /**
   * The pile has resolved and is about to be swept. Hold the readout for a beat
   * so the player can see *what* it was, then let it go with the cards.
   */
  flourish(kind: Flourish): void {
    this.#cancelHold();
    if (!this.#revealed) return;
    this.#popTo(kind === 'bust' ? POP_PLAY : POP_CLEAR);
    const wait = (kind === 'bust' ? motion.bust : motion.clear) * 0.62;
    this.#hold = gsap.delayedCall(wait, () => {
      this.#hold = null;
      this.#hide(true);
    });
  }

  /** Rebuild text styles — used when the display face finishes loading. */
  restyle(): void {
    this.#styleDirty = true;
    if (this.#primaryValue !== null) this.#refreshText();
  }

  override destroy(options?: DestroyOptions): void {
    this.#cancelHold();
    gsap.killTweensOf(this.#pop);
    gsap.killTweensOf(this.#paint);
    gsap.killTweensOf(this);
    gsap.killTweensOf(this.scale);
    gsap.killTweensOf(this.#body.scale);
    for (const gradient of this.#gradients) gradient.destroy();
    this.#gradients.length = 0;
    // The shared metal gradients are cached module-wide — never destroy those.
    for (const filter of [this.#shadow.filters, this.#glow.filters].flat()) {
      filter?.destroy();
    }
    this.#shadow.filters = [];
    this.#glow.filters = [];
    super.destroy(options);
  }

  // ---------------------------------------------------------------- drawing

  #drawPlaque(): void {
    const { halfWidth: hw, halfHeight: hh, width: w, height: h, radius: r } = BADGE;

    const body = plaqueGradient(this.#dir);
    const gloss = glossGradient(this.#dir);
    const hairline = hairlineGradient(this.#dir);
    this.#gradients.push(body, gloss, hairline);

    this.#plaque.roundRect(-hw, -hh, w, h, r).fill(body);
    this.#gloss.roundRect(-hw, -hh, w, h, r).fill(gloss);
    this.#hairline
      .roundRect(-hw, -hh, w, h, r)
      .stroke({ width: 1.25, fill: hairline, alignment: 0.5, join: 'round' });
  }

  #drawTrack(): void {
    const r = GAUGE.height / 2;
    this.#track
      .roundRect(GAUGE.x, GAUGE.y, GAUGE.width, GAUGE.height, r)
      .fill({ color: palette.ink, alpha: 0.62 })
      .stroke({ width: 1, color: palette.white, alpha: 0.07, alignment: 1 });

    // Notches on the thresholds the colour steps on, so the gauge is readable
    // as a position and not just a length.
    for (const mark of [CAUTION_AT, DANGER_AT]) {
      const x = GAUGE.x + (GAUGE.width * mark) / TARGET;
      this.#track
        .rect(x - 0.5, GAUGE.y + GAUGE.height * 0.2, 1, GAUGE.height * 0.6)
        .fill({ color: palette.white, alpha: 0.18 });
    }
  }

  /** The `▾` in front of the hard total, drawn rather than typeset so it is
   *  always there and always the right weight. */
  #drawCaret(): void {
    const w = BADGE.subSize * 0.44;
    const h = BADGE.subSize * 0.3;
    this.#caret.poly([-w / 2, -h / 2, w / 2, -h / 2, 0, h / 2]).fill(palette.white);
    this.#caret.alpha = 0.85;
  }

  #drawGauge(): void {
    const g = this.#gauge.clear();
    const glow = this.#glow.clear();
    const r = GAUGE.height / 2;
    const level = Math.min(Math.max(this.#paint.fill, 0), 1);
    const bar = Math.min(Math.max(level * GAUGE.width, GAUGE.height), GAUGE.width);

    if (level <= 0) {
      this.#gauge.visible = false;
      this.#glow.visible = false;
      return;
    }
    this.#gauge.visible = true;
    this.#glow.visible = true;

    const colour = this.#paint.colour;

    g.roundRect(GAUGE.x, GAUGE.y, bar, GAUGE.height, r).fill({ color: colour, alpha: 0.94 });
    // Lit upper lip: the light is above the table, so the bar catches it there.
    g.roundRect(
      GAUGE.x + r * 0.5,
      GAUGE.y + GAUGE.height * 0.16,
      Math.max(bar - r, 1),
      GAUGE.height * 0.24,
      GAUGE.height * 0.12,
    ).fill({ color: mix(colour, palette.white, 0.72), alpha: 0.55 });
    // The meniscus — a bright leading edge reads as a level, not a smear.
    g.roundRect(
      GAUGE.x + bar - r * 0.7,
      GAUGE.y - GAUGE.height * 0.24,
      r * 0.6,
      GAUGE.height * 1.48,
      r * 0.3,
    ).fill({ color: mix(colour, palette.white, 0.85), alpha: 0.95 });

    glow.roundRect(GAUGE.x, GAUGE.y, bar, GAUGE.height, r).fill({ color: colour, alpha: 0.85 });
  }

  // ----------------------------------------------------------------- state

  #refreshText(): void {
    if (this.#primaryValue === null) return;

    const soft = this.#hardUnder !== null;
    const size = soft ? BADGE.softSize : BADGE.hardSize;

    if (this.#styleDirty || this.#styledRisk !== this.#risk || this.#styledSoft !== soft) {
      this.#styleDirty = false;
      this.#styledRisk = this.#risk;
      this.#styledSoft = soft;
      this.#primary.style = numberStyle(size, this.#risk, this.#shadowAngle);
      this.#primaryLit.style = litStyle(size);
      this.#sub.style = subStyle(this.#shadowAngle);
    }

    const label = String(this.#primaryValue);
    this.#primary.text = label;
    this.#primaryLit.text = label;
    this.#sub.text = this.#hardUnder === null ? '' : String(this.#hardUnder);

    this.#layoutText();
  }

  #layoutText(): void {
    const soft = this.#hardUnder !== null;

    if (soft) {
      this.#primary.x = 0;
      this.#primary.y = READOUT_MID - BADGE.height * 0.125 + BADGE.softSize * OPTICAL_NUDGE;

      this.#sub.visible = true;
      this.#caret.visible = true;

      // Centre the caret and the hard total as one lockup.
      const y = READOUT_MID + BADGE.height * 0.215;
      const caretWidth = BADGE.subSize * 0.44;
      const gap = BADGE.subSize * 0.26;
      const textWidth = this.#sub.width;
      const total = caretWidth + gap + textWidth;
      this.#caret.x = -total / 2 + caretWidth / 2;
      this.#caret.y = y;
      this.#sub.x = total / 2 - textWidth / 2;
      this.#sub.y = y + BADGE.subSize * OPTICAL_NUDGE;
    } else {
      this.#primary.x = 0;
      this.#primary.y = READOUT_MID + BADGE.hardSize * OPTICAL_NUDGE;
      this.#sub.visible = false;
      this.#caret.visible = false;
    }

    this.#primaryLit.x = this.#primary.x + this.#lit.x;
    this.#primaryLit.y = this.#primary.y + this.#lit.y;
  }

  /** Push the animated colour into everything tinted by risk. */
  #applyPaint(): void {
    const colour = this.#paint.colour;
    this.#hairline.tint = colour;
    this.#caret.tint = mix(colour, palette.white, 0.45);
    this.#sub.tint = mix(colour, palette.white, 0.6);
    this.#drawGauge();
  }

  /** Push the animated lift into the scale and, from it, the shadow. */
  #applyPop(): void {
    this.#body.scale.set(this.#pop.scale);
    const cast = castShadow(this.x, this.y, this.#pop.lift);
    this.#shadow.position.set(cast.dx, cast.dy);
    this.#shadow.scale.set(1 + (this.#pop.scale - 1) * 1.15);
    this.#shadow.alpha = cast.alpha;
    this.#shadowBlur.strength = cast.blur;
  }

  #popTo(strength: number): void {
    gsap.killTweensOf(this.#pop);
    this.#pop.scale = 1 + strength;
    this.#pop.lift = LIFT_REST + (LIFT_POP - LIFT_REST) * Math.min(strength / POP_CLEAR, 1);
    this.#applyPop();
    gsap.to(this.#pop, {
      scale: 1,
      lift: LIFT_REST,
      duration: motion.settle.duration,
      ease: motion.settle.ease,
      onUpdate: () => this.#applyPop(),
    });
  }

  #show(animate: boolean): void {
    if (this.#revealed) return;
    this.#revealed = true;
    this.visible = true;
    gsap.killTweensOf(this);
    gsap.killTweensOf(this.scale);

    if (!animate) {
      this.alpha = 1;
      this.scale.set(1);
      return;
    }
    this.alpha = 0;
    this.scale.set(0.86);
    gsap.to(this, { alpha: 1, duration: motion.ui, ease: 'power2.out' });
    gsap.to(this.scale, {
      x: 1,
      y: 1,
      duration: motion.settle.duration,
      ease: motion.settle.ease,
    });
  }

  #hide(animate: boolean): void {
    this.#primaryValue = null;
    this.#hardUnder = null;
    if (!this.#revealed) {
      this.visible = false;
      this.alpha = 0;
      return;
    }
    this.#revealed = false;
    gsap.killTweensOf(this);
    gsap.killTweensOf(this.scale);
    gsap.killTweensOf(this.#pop);
    // Nothing is watching a hidden plaque — stop redrawing its gauge.
    gsap.killTweensOf(this.#paint);
    this.#pop.scale = 1;
    this.#pop.lift = LIFT_REST;
    this.#applyPop();

    if (!animate) {
      this.alpha = 0;
      this.visible = false;
      return;
    }
    gsap.to(this, {
      alpha: 0,
      duration: motion.ui * 0.8,
      ease: motion.exit,
      onComplete: () => {
        this.visible = false;
      },
    });
    gsap.to(this.scale, { x: 0.9, y: 0.9, duration: motion.ui * 0.8, ease: motion.exit });
  }

  #cancelHold(): void {
    this.#hold?.kill();
    this.#hold = null;
  }
}

export interface PileTotalsOptions {
  /** Bus to listen on. Defaults to the shared app bus. */
  bus?: EventBus;
  /** Subscribe on construction. Defaults to true. */
  bind?: boolean;
}

/**
 * The four pile readouts, as one container.
 *
 * Drop it into a layer above the cards and it looks after itself:
 *
 * ```ts
 * const totals = new PileTotals();
 * layers.ui.addChild(totals);
 * ```
 *
 * It listens only for the five events that can change a pile — `game:start`,
 * `card:play`, `pile:resolve`, `undo` and `game:over`. `tick` fires at 60Hz and
 * would rebuild four plaques every frame for a number that changes twice a
 * second, so it is deliberately not subscribed.
 */
export class PileTotals extends Container {
  readonly #badges: TotalBadge[] = [];

  /** Re-place every badge after `@/pixi/layout` has switched orientation. */
  /**
   * Draw all four badges once, invisibly, so the first card played is not the
   * frame that builds them.
   *
   * A badge is `visible = false` until its pile has a card, and it is not a
   * cheap object: a plaque, a gloss, a hairline, a track, a gauge and a caret in
   * `Graphics`, three `Text` objects, and two `BlurFilter`s — one for the drop
   * shadow, one for the gauge glow. None of that is compiled, rasterised or
   * uploaded until the badge is first drawn, and the frame it is first drawn on
   * is the frame the player puts their first card on a pile.
   *
   * Warmed in both layouts — the single big figure, and the pair a soft ace
   * needs — because they use different text sizes and therefore different
   * rasterisations.
   */
  warm(): void {
    this.#badges.forEach((badge, i) => {
      // A soft hand on half of them, so the two-figure layout is built too.
      badge.apply(i % 2 === 0 ? { hard: 17, soft: 17 } : { hard: 7, soft: 17 }, false);
      badge.alpha = WARM_ALPHA;
    });
  }

  /** Undo {@link warm}. */
  cool(): void {
    for (const badge of this.#badges) {
      badge.alpha = 1;
      badge.apply(null, false);
    }
  }

  relayout(): void {
    for (const badge of this.#badges) badge.relayout();
  }

  readonly #unsubscribe: Array<() => void> = [];
  readonly #bus: EventBus;
  #fontRefresh: (() => void) | null = null;

  constructor(options: PileTotalsOptions = {}) {
    super({ label: 'pile-totals' });
    // Purely a readout — it must never swallow a click meant for a pile.
    this.eventMode = 'none';
    this.#bus = options.bus ?? sharedBus;

    for (let i = 0; i < PILE_COUNT; i++) {
      const badge = new TotalBadge(i);
      this.#badges.push(badge);
      this.addChild(badge);
    }

    if (options.bind !== false) this.bind();
    this.#watchFonts();
  }

  /** Subscribe to the bus. Safe to call twice; the second call is a no-op. */
  bind(): void {
    if (this.#unsubscribe.length > 0) return;
    this.#unsubscribe.push(
      this.#bus.on('game:start', (event) => this.sync(event.snapshot, false)),
      this.#bus.on('card:play', (event) => this.sync(event.snapshot, true)),
      this.#bus.on('pile:resolve', (event) => {
        const { resolution, snapshot } = event;
        for (let i = 0; i < this.#badges.length; i++) {
          // A cleared pile is already empty in the snapshot, but the cards are
          // still on the table for another half second. Let the badge finish
          // its sentence rather than blinking out mid-word.
          if (i === resolution.pile && resolution.cleared) {
            this.#badges[i].flourish(resolution.isBust ? 'bust' : 'clear');
          } else {
            this.#badges[i].apply(totalsFor(snapshot, i), true);
          }
        }
      }),
      this.#bus.on('undo', (event) => this.sync(event.snapshot, true)),
      this.#bus.on('game:over', (event) => this.sync(event.snapshot, true)),
    );
  }

  /** Drop every bus subscription. The container stays usable via `sync`. */
  unbind(): void {
    for (const off of this.#unsubscribe) off();
    this.#unsubscribe.length = 0;
  }

  /** Redraw every badge from a snapshot. Idempotent. */
  sync(snapshot: GameSnapshot, animate = true): void {
    for (let i = 0; i < this.#badges.length; i++) {
      this.#badges[i].apply(totalsFor(snapshot, i), animate);
    }
  }

  /** Hide all four, without waiting for an event. */
  reset(animate = false): void {
    for (const badge of this.#badges) badge.apply(null, animate);
  }

  override destroy(options?: DestroyOptions): void {
    this.unbind();
    if (this.#fontRefresh && typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.removeEventListener('loadingdone', this.#fontRefresh);
    }
    this.#fontRefresh = null;
    this.#badges.length = 0;
    // The badges are wholly owned here, and they hold gradient textures and
    // blur filters that only their own destroy() releases.
    super.destroy(typeof options === 'boolean' ? options : { ...options, children: true });
  }

  /**
   * Pixi measures text against whatever face is resolved at the moment the
   * glyph texture is baked. The display face is a local `@font-face`, which the
   * browser will not fetch until something asks for it — and a canvas
   * measurement does not count as asking. So ask, then re-bake.
   */
  #watchFonts(): void {
    if (typeof document === 'undefined' || !('fonts' in document)) return;
    const fonts = document.fonts;

    const refresh = (): void => {
      for (const badge of this.#badges) badge.restyle();
    };
    this.#fontRefresh = refresh;
    fonts.addEventListener('loadingdone', refresh);

    void fonts.ready
      .then(() => fonts.load(`${Math.round(BADGE.hardSize)}px ${DISPLAY_FONT}`))
      .then(refresh)
      .catch(() => {
        // No local face registered yet — the fallback stack already rendered,
        // and `loadingdone` will still catch it if one arrives later.
      });
  }
}

/** A pile's totals, or null when the pile is empty and the badge should go. */
function totalsFor(snapshot: GameSnapshot, index: number): PileTotalValues | null {
  const pile = snapshot.piles[index];
  if (!pile || pile.length === 0) return null;
  return snapshot.pileTotals[index] ?? null;
}

/** Convenience factory, matching the shape of the other pixi modules. */
export function createPileTotals(options?: PileTotalsOptions): PileTotals {
  return new PileTotals(options);
}
